import { useState, useEffect } from 'react';
import { Mail, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import { adminGetEmails } from '@/lib/api';
import { toast } from 'sonner';

const AdminEmails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    try {
      const data = await adminGetEmails();
      setEmails(data || []);
    } catch (error) {
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const exportEmails = () => {
    const csv = ['Email,Date'].concat(
      emails.map(e => `${e.email},${e.created_at}`)
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-captures-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Emails exported');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading emails...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Email Captures</h1>
          <p className="text-stone-600 text-sm">{emails.length} emails collected</p>
        </div>
        {emails.length > 0 && (
          <Button onClick={exportEmails} variant="outline" className="btn-outline" data-testid="export-emails-btn">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Export CSV
          </Button>
        )}
      </div>

      {emails.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-stone-500">No emails captured yet</p>
            <p className="text-sm text-stone-400 mt-2">
              Emails will appear here when visitors complete the quiz
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Date Captured</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} data-testid={`email-row-${email.id}`}>
                  <TableCell className="font-medium">{email.email}</TableCell>
                  <TableCell className="text-stone-500">{formatDate(email.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default AdminEmails;
